<script>
  // Полноэкранный просмотр текста сообщения для выделения и копирования (1.23.1).
  //
  // В ленте у пузыря user-select:none и запрет системного callout — тап и удержание
  // открывают шторку, и системное выделение конфликтовало бы с жестами и свайпом.
  // Здесь текст выделяется нативно, меню iOS «Скопировать» работает само. Затемнения
  // с закрытием по тапу нет: соскок пальца с маркера выделения не должен закрывать экран.
  import { createEventDispatcher, tick } from 'svelte';
  import { renderMarkdown } from '../markdown.js';
  import { TEXT } from '../ui-text.js';

  export let open = false;
  export let title = '';
  export let text = '';
  // 'plain' — user/system как есть, 'markdown' — ответ Петровича и конспект этапа.
  export let mode = 'plain';

  const dispatch = createEventDispatcher();

  let panelElement = null;
  let previouslyFocused = null;

  $: rendered = mode === 'markdown' ? renderMarkdown(text) : '';

  $: if (open) {
    focusPanel();
  } else {
    restoreFocus();
  }

  async function focusPanel() {
    previouslyFocused = typeof document !== 'undefined' ? document.activeElement : null;
    await tick();
    panelElement?.focus();
  }

  function restoreFocus() {
    // На поле ввода фокус не возвращаем: iOS поднял бы клавиатуру сразу после «Готово».
    if (
      previouslyFocused
      && typeof previouslyFocused.focus === 'function'
      && !previouslyFocused.matches?.('textarea, input')
    ) {
      previouslyFocused.focus();
    }
    previouslyFocused = null;
  }

  function close() {
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab' || !panelElement) {
      return;
    }
    const focusable = panelElement.querySelectorAll('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
</script>

{#if open}
  <div
    class="viewer"
    bind:this={panelElement}
    role="dialog"
    aria-modal="true"
    aria-label={TEXT.chat.selectTextTitle}
    tabindex="-1"
    data-no-swipe
    data-testid="text-viewer"
    on:keydown={handleKeydown}
  >
    <header class="viewer-head">
      <span class="viewer-title">{title || TEXT.chat.selectTextTitle}</span>
      <button type="button" class="viewer-done" on:click={close}>{TEXT.chat.selectTextDone}</button>
    </header>
    <div class="viewer-body" data-testid="text-viewer-body">
      {#if mode === 'markdown'}
        <div class="markdown-body">{@html rendered}</div>
      {:else}
        <p class="viewer-plain">{text}</p>
      {/if}
    </div>
    <footer class="viewer-foot">
      <button type="button" class="viewer-copy" on:click={() => dispatch('copy')}>{TEXT.chat.selectTextCopyAll}</button>
      <button type="button" class="viewer-done-secondary" on:click={close}>{TEXT.chat.selectTextDone}</button>
    </footer>
  </div>
{/if}

<style>
  .viewer {
    position: fixed;
    inset: 0;
    /* Выше ленты и плавающих кнопок, ниже флеша (App.svelte, 200): «Текст скопирован.»
       должен быть виден поверх просмотра. Шторки (300) к этому моменту закрыты. */
    z-index: 190;
    height: var(--app-height, 100dvh);
    display: flex;
    flex-direction: column;
    background: var(--bg-primary);
    color: var(--text-primary);
    padding-top: env(safe-area-inset-top, 0px);
    outline: none;
  }

  .viewer-head,
  .viewer-foot {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex: none;
    background: var(--bg-secondary);
    -webkit-user-select: none;
    user-select: none;
  }

  .viewer-head {
    justify-content: space-between;
    padding: 0.6rem var(--spacing-lg);
    border-bottom: 1px solid var(--border-color);
  }

  .viewer-title {
    font-size: 0.9rem;
    color: var(--text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .viewer-foot {
    padding: 0.75rem var(--spacing-lg) calc(0.75rem + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--border-color);
  }

  .viewer-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    touch-action: pan-y;
    padding: var(--spacing-lg);
    padding-left: calc(var(--spacing-lg) + env(safe-area-inset-left, 0px));
    padding-right: calc(var(--spacing-lg) + env(safe-area-inset-right, 0px));
    /* Конец текста не должен лежать под подвалом — маркер выделения у края залипает. */
    padding-bottom: 3rem;
    -webkit-user-select: text;
    user-select: text;
    -webkit-touch-callout: default;
    cursor: text;
    font-size: max(16px, 1rem);
    line-height: 1.55;
  }

  .viewer-plain {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .viewer-done,
  .viewer-copy,
  .viewer-done-secondary {
    min-height: 44px;
    padding: 0.5rem 1rem;
    border-radius: 12px;
    border: 1px solid var(--accent-color);
    background: var(--bg-primary);
    color: var(--accent-color);
    font-size: 1rem;
    cursor: pointer;
    -webkit-user-select: none;
    user-select: none;
  }

  .viewer-copy,
  .viewer-done-secondary {
    flex: 1;
    min-height: 48px;
  }

  .viewer-copy {
    background: var(--accent-color);
    color: var(--bg-primary);
  }

  /* Ссылки читаются и копируются как текст: тап по ним при снятии выделения никуда не ведёт. */
  .viewer-body :global(a) {
    pointer-events: none;
    color: var(--accent-color);
    text-decoration: underline;
    cursor: text;
  }

  .viewer-body :global(a[data-source-message]),
  .viewer-body :global(a[data-source-stage]) {
    color: var(--text-muted);
    text-decoration: none;
    font-size: 0.85em;
    vertical-align: super;
  }

  .viewer-body :global(p) {
    margin: 0 0 0.75em;
  }

  .viewer-body :global(pre) {
    margin: 0.5em 0;
    padding: 0.75em;
    border-radius: 8px;
    background: var(--bg-tertiary);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.9em;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .viewer-body :global(code) {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.9em;
  }

  .viewer-body :global(ul),
  .viewer-body :global(ol) {
    padding-left: 1.4em;
    margin: 0 0 0.75em;
  }

  .viewer-body :global(blockquote) {
    margin: 0.5em 0;
    padding-left: 0.75em;
    border-left: 3px solid var(--border-color);
    color: var(--text-secondary);
  }

  .viewer-body :global(.table-wrap) {
    overflow-x: auto;
    margin: 0.5em 0;
  }

  .viewer-body :global(table) {
    border-collapse: collapse;
  }

  .viewer-body :global(th),
  .viewer-body :global(td) {
    border: 1px solid var(--border-color);
    padding: 0.3em 0.5em;
  }

  .viewer-body :global(h1),
  .viewer-body :global(h2),
  .viewer-body :global(h3) {
    font-size: 1.05em;
    margin: 0.8em 0 0.3em;
  }
</style>
